import { HttpErrorResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { NotificationType } from 'angular2-notifications'
import { of } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { ApplicationRoutesEnum, FeaturesRoutesEnum } from '../../models/enums/application-routes.enum'
import { LogService } from '../../services/log.service'
import { NotificationService } from '../../services/notification.service'
import * as WithClientsActions from './with-clients.actions'
import { WithClientsService } from './with-clients.service'

@Injectable()
export class WithClientsEffects {
   private _cn = this.constructor.name
   private _router = inject(Router)
   private _actions$ = inject(Actions)
   private _logService = inject(LogService)
   private _withClientsService = inject(WithClientsService)
   private _notificationService = inject(NotificationService)

   getWithClientsData$ = createEffect(() =>
      this._actions$.pipe(
         ofType(WithClientsActions.getWithClientsData),
         switchMap((action) =>
            this._withClientsService.searchWithClientsData(action.request).pipe(
               tap((res) => this._logService.debug(this._cn, 'getWithClientsData effect', 'response:', res)),
               map((WithClientsItems) => WithClientsActions.getWithClientsDataSuccess({ data: WithClientsItems })),
               catchError((error: HttpErrorResponse) => {
                  return of(WithClientsActions.getWithClientsDataFailure({ error: error }))
               })
            )
         )
      )
   )

   markWithClientsDataAsProcessed$ = createEffect(() =>
      this._actions$.pipe(
         ofType(WithClientsActions.markWithClientsDataAsProcessed),
         switchMap((action) =>
            this._withClientsService.markWithClientsDataAsProcessed(action.id).pipe(
               tap((res) => this._logService.debug(this._cn, 'mark Linking With Clients Data As Processed effect', 'response:', res)),
               map(() => {
                  return WithClientsActions.markWithClientsDataAsProcessedSuccess()
               }),
               catchError((error: HttpErrorResponse) => {
                  return of(WithClientsActions.markWithClientsDataAsProcessedFailure({ error: error }))
               })
            )
         )
      )
   )

   deleteWithClientsData$ = createEffect(() =>
      this._actions$.pipe(
         ofType(WithClientsActions.deleteWithClientsData),
         switchMap((action) =>
            this._withClientsService.deleteWithClientsData(action.id).pipe(
               tap((res) => this._logService.debug(this._cn, 'deleteWithClientsData effect', 'response:', res)),
               map(() => WithClientsActions.deleteWithClientsDataSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(WithClientsActions.deleteWithClientsDataFailure({ error: error }))
               })
            )
         )
      )
   )

   getWithClientsDataByID$ = createEffect(() =>
      this._actions$.pipe(
         ofType(WithClientsActions.getWithClientsDetailByID),
         switchMap((action) =>
            this._withClientsService.getWithClientsDataByID(action.id).pipe(
               tap((res) => this._logService.debug(this._cn, 'getWithClientsDataByID effect', 'response:', res)),
               map((WithClientsDetail) => WithClientsActions.getWithClientsDetailByIDSuccess({ data: WithClientsDetail })),
               catchError((error: HttpErrorResponse) => {
                  return of(WithClientsActions.getWithClientsDetailByIDFailure({ error: error }))
               })
            )
         )
      )
   )

   /* Notification error message */
   private _actionsForErrorNotificationMessage = [
      WithClientsActions.getWithClientsDataFailure,
      WithClientsActions.markWithClientsDataAsProcessedFailure,
      WithClientsActions.deleteWithClientsDataFailure,
      WithClientsActions.getWithClientsDetailByIDFailure,
   ]

   listenAllFailureAction$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsForErrorNotificationMessage),
            map((errorResponse) => {
               if (this._router.url.includes('linking-with-clients/detail')) {
                  this._router.navigate([
                     `${ApplicationRoutesEnum.MAIN_PAGE}/${FeaturesRoutesEnum.LINKING_WITH_CLIENTS}/${FeaturesRoutesEnum.LINKING_WITH_CLIENTS_SEARCH}`,
                  ])
               }

               this._notificationService.notify(NotificationType.Error, undefined, `${errorResponse.error.status} - ${errorResponse.error.statusText}`)
            })
         ),
      { dispatch: false }
   )

   /* Notification success message */
   private _actionsForSuccessNotificationMessage = [WithClientsActions.markWithClientsDataAsProcessedSuccess, WithClientsActions.deleteWithClientsDataSuccess]

   listenAllSuccessAction$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsForSuccessNotificationMessage),
            map(() => this._notificationService.notify(NotificationType.Success))
         ),
      { dispatch: false }
   )
}
